import { Link } from "react-router-dom";
import { ArrowUpRight, Landmark, Building2, MapPin } from "lucide-react";
import Reveal from "@/components/Reveal";
import { COLLECTIVITES } from "@/lib/siteContent";

const HERO_IMAGE =
    "https://images.pexels.com/photos/30297108/pexels-photo-30297108.jpeg?auto=compress&cs=tinysrgb&w=2400";
const FRANCE_IMAGE =
    "https://images.pexels.com/photos/14079666/pexels-photo-14079666.jpeg?auto=compress&cs=tinysrgb&w=2400";

export default function Collectivites() {
    return (
        <div data-testid="collectivites-page" className="bg-xv-ivory">
            {/* HERO */}
            <section
                data-testid="collectivites-hero"
                className="relative pt-32 md:pt-44 pb-24 md:pb-32 overflow-hidden bg-xv-black text-xv-ivory"
            >
                <div className="absolute inset-0">
                    <img
                        src={HERO_IMAGE}
                        alt="Acteurs institutionnels & territoires"
                        className="w-full h-full object-cover opacity-35"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-xv-black/70 via-xv-black/60 to-xv-black/95" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Acteurs institutionnels & territoires
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h1
                            className="font-serif text-xv-ivory text-[44px] sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.98] tracking-tight max-w-5xl"
                            style={{ fontWeight: 300 }}
                        >
                            Accompagner les territoires{" "}
                            <em className="not-italic text-xv-goldSoft">
                                dans leurs grandes opérations.
                            </em>
                        </h1>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-12 max-w-3xl text-xv-ivory/80 text-[17px] md:text-[19px] leading-[1.8] font-light">
                            {COLLECTIVITES.intro}
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* PRINCIPES */}
            <section
                data-testid="fund-facts"
                className="bg-xv-cream py-28 md:py-36"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-16 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Quatre principes d'intervention
                            </span>
                        </div>
                    </Reveal>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
                        {COLLECTIVITES.fundFacts.map((f, i) => (
                            <Reveal key={f.label} delay={i * 0.08}>
                                <div
                                    data-testid={`fund-fact-${i}`}
                                    className="border-t border-xv-midnight/30 pt-8"
                                >
                                    <div
                                        className="font-serif text-[28px] md:text-[40px] lg:text-[44px] leading-tight text-xv-black"
                                        style={{ fontWeight: 300 }}
                                    >
                                        {f.value}
                                        <span className="text-xv-midnight">
                                            {f.suffix}
                                        </span>
                                    </div>
                                    <p className="overline text-xv-gold mt-6">
                                        {f.label}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROGRAMME */}
            <section
                data-testid="program"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <div className="flex items-center gap-4 mb-8 text-xv-gold">
                                <span className="divider-gold" />
                                <span className="overline">
                                    {COLLECTIVITES.program.title}
                                </span>
                            </div>
                            <h2
                                className="font-serif text-xv-black text-[40px] md:text-5xl lg:text-[58px] leading-[1.04] mb-10"
                                style={{ fontWeight: 300 }}
                            >
                                Une architecture{" "}
                                <em className="not-italic text-xv-midnight">
                                    sur-mesure
                                </em>{" "}
                                pour chaque opération.
                            </h2>
                            <p className="text-xv-text-secondary text-[16px] leading-[1.85] font-light max-w-md mb-10">
                                Le cabinet coordonne, pour chaque dossier
                                territorial, l'écosystème international
                                d'experts indépendants et de partenaires
                                de long terme adapté — depuis le cadrage
                                stratégique jusqu'à la sécurisation des
                                conditions d'exécution.
                            </p>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-7 lg:pl-10">
                        <Reveal delay={0.1}>
                            <div className="overline text-xv-gold mb-8">
                                Périmètres d'intervention
                            </div>
                            <div className="space-y-px bg-xv-line">
                                {COLLECTIVITES.program.eligibles.map(
                                    (e, i) => (
                                        <div
                                            key={e}
                                            data-testid={`eligible-${i}`}
                                            className="flex items-start gap-6 bg-xv-ivory px-6 py-7"
                                        >
                                            <Building2
                                                strokeWidth={1}
                                                size={26}
                                                className="text-xv-midnight mt-1 shrink-0"
                                            />
                                            <p className="font-serif text-[20px] md:text-[24px] text-xv-black leading-snug">
                                                {e}
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* APPROCHE */}
            <section
                data-testid="conditions"
                className="relative bg-xv-midnightDeep text-xv-ivory py-28 md:py-40 overflow-hidden"
            >
                <div className="absolute inset-0 opacity-20 pointer-events-none">
                    <img
                        src={FRANCE_IMAGE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-midnightDeep/80" />
                </div>
                <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-16 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Notre approche
                            </span>
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h2
                            className="font-serif text-[40px] md:text-5xl lg:text-[60px] leading-[1.04] mb-20 max-w-3xl"
                            style={{ fontWeight: 300 }}
                        >
                            Un cadre{" "}
                            <em className="not-italic text-xv-goldSoft">
                                exigeant et discret.
                            </em>
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-x-16 gap-y-10">
                        {COLLECTIVITES.conditions.map((c, i) => (
                            <Reveal key={c.k} delay={i * 0.06}>
                                <div
                                    data-testid={`condition-${i}`}
                                    className="border-t border-xv-goldSoft/30 pt-6 grid grid-cols-3 gap-6"
                                >
                                    <div className="overline text-xv-goldSoft col-span-1">
                                        {c.k}
                                    </div>
                                    <div
                                        className="font-serif text-[20px] md:text-[24px] text-xv-ivory col-span-2 leading-snug"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {c.v}
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* POURQUOI LA FRANCE */}
            <section
                data-testid="why-france"
                className="bg-xv-ivory py-28 md:py-40"
            >
                <div className="max-w-[1100px] mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="flex items-center gap-4 mb-10 text-xv-gold">
                            <span className="divider-gold" />
                            <span className="overline">
                                Ancrage France
                            </span>
                        </div>
                        <h2
                            className="font-serif text-xv-black text-[34px] md:text-5xl lg:text-[58px] leading-[1.05] mb-16"
                            style={{ fontWeight: 300 }}
                        >
                            Une signature publique{" "}
                            <em className="not-italic text-xv-midnight">
                                de premier rang.
                            </em>
                        </h2>
                    </Reveal>
                    <div className="space-y-px bg-xv-line">
                        {COLLECTIVITES.rationale.map((r, i) => (
                            <Reveal key={r} delay={i * 0.08}>
                                <div
                                    data-testid={`rationale-${i}`}
                                    className="bg-xv-ivory py-8 px-6 flex items-start gap-8"
                                >
                                    <Landmark
                                        strokeWidth={1}
                                        size={26}
                                        className="text-xv-gold mt-1 shrink-0"
                                    />
                                    <p
                                        className="font-serif text-[22px] md:text-[28px] text-xv-black leading-snug max-w-3xl"
                                        style={{ fontWeight: 400 }}
                                    >
                                        {r}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="relative bg-xv-black text-xv-ivory py-28 md:py-36 overflow-hidden">
                <div className="absolute inset-0 opacity-25">
                    <img
                        src={HERO_IMAGE}
                        alt=""
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-xv-black/75" />
                </div>
                <div className="relative max-w-[1100px] mx-auto px-6 md:px-12 text-center">
                    <Reveal>
                        <div className="inline-flex items-center gap-4 mb-10 text-xv-goldSoft">
                            <span className="divider-gold" />
                            <span className="overline">
                                Territoires · Premier contact
                            </span>
                            <span className="divider-gold" />
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2
                            className="font-serif text-[40px] md:text-5xl lg:text-[68px] leading-[1.04]"
                            style={{ fontWeight: 300 }}
                        >
                            Un projet de territoire{" "}
                            <em className="not-italic text-xv-goldSoft">
                                à structurer ?
                            </em>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <p className="mt-10 text-xv-ivory/75 max-w-2xl mx-auto leading-[1.85] font-light">
                            Le cabinet conduit la phase de cadrage
                            stratégique en parfaite confidentialité,
                            avant toute coordination du réseau
                            d'experts et de partenaires internationaux.
                        </p>
                    </Reveal>
                    <Reveal delay={0.3}>
                        <Link
                            to="/contact"
                            data-testid="collectivites-cta"
                            className="group mt-14 inline-flex items-center gap-4 px-10 py-5 bg-xv-ivory text-xv-midnightDeep text-[11px] tracking-[0.3em] uppercase hover:bg-xv-gold transition-all duration-700"
                        >
                            Demander un premier entretien
                            <ArrowUpRight
                                strokeWidth={1.2}
                                size={16}
                                className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                            />
                        </Link>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
